import { KULUTTAJAMYYNNIN_LAJIT_EUHUN, KULUTTAJAN_VAHIMMAISMAKSUAIKA, LaskunAsiakas, LaskunAsiakastyyppi, LaskunLahetystapa, LaskunTyypit, LaskunTyyppi } from '@lemon/core-firebase/model/lasku'
import { YtjToimittaja } from '@lemon/core-firebase/model/web-toimittaja-api'
import { Verkkolaskuoperaattori } from '@lemon/core-firebase/service/lasku/sahkoisen-laskun-valittaja.service'
import { TuettuKieli } from '@lemon/core/model/common'
import type { LaskuAsiakasDialogTulos } from './lasku-asiakas.dialog'

export interface AsiakasLomake {
  nimi: string
  ytunnus: string
  katuosoite: string
  postinro: string
  postitoimipaikka: string
  maa: string | null
  tyyppi: LaskunTyyppi
  valuutta: string
  kieli: TuettuKieli
  oletusLahetystapa: LaskunLahetystapa
  maksuaikaValinta: number | 'muu'
  maksuaikaa: number
  verkkolaskuosoite: string
  verkkolaskuvalittaja: string | Verkkolaskuoperaattori | null
  lisaavastaanottaja: string
  laskutusprofiiliAvain: string | null
}

export type AsiakastyypinOletukset = Partial<Pick<AsiakasLomake, 'maksuaikaValinta' | 'maksuaikaa' | 'tyyppi' | 'oletusLahetystapa'>>

export type YtjOsoitteenKaytto = 'tayta' | 'ehdota' | 'ei-mitaan'

export interface LomakkeenOsoite {
  katuosoite: string
  postinro: string
  postitoimipaikka: string
}

export interface VerkkolaskuosoitteenHaunTiedot {
  ytunnus: string
  lahetystapa: LaskunLahetystapa
  onkoYtunnusKelvollinen: boolean
  haettuYtunnus: string | null
  nykyinenOsoite: string
  haettuOsoite: string | null
}

export function onkoMyynninLajiSallittu(tyyppi: LaskunTyyppi | null, asiakastyyppi: LaskunAsiakastyyppi | null): boolean {
  const onkoKuluttajamyyntiEuhun = KULUTTAJAMYYNNIN_LAJIT_EUHUN.includes(tyyppi?.avain)
  if (asiakastyyppi === LaskunAsiakastyyppi.HENKILO) {
    return tyyppi?.avain === LaskunTyypit.TAVALLINEN.avain || tyyppi?.avain === LaskunTyypit.MUU_MAAILMA.avain || onkoKuluttajamyyntiEuhun
  }
  if (asiakastyyppi === LaskunAsiakastyyppi.YRITYS) {
    return !onkoKuluttajamyyntiEuhun
  }
  return true
}

export function onkoAsiakkaanOsoitePakollinen(asiakastyyppi: LaskunAsiakastyyppi | null): boolean {
  return asiakastyyppi !== LaskunAsiakastyyppi.HENKILO
}

export function onkoAsiakkaanMaaPakollinen(asiakastyyppi: LaskunAsiakastyyppi | null, tyyppi: LaskunTyyppi | null): boolean {
  const onkoKuluttajamyyntiEuhun = !!tyyppi && KULUTTAJAMYYNNIN_LAJIT_EUHUN.includes(tyyppi.avain)
  return onkoAsiakkaanOsoitePakollinen(asiakastyyppi) || onkoKuluttajamyyntiEuhun
}

export function annaOletuslahetystapa(asiakas: LaskunAsiakas | null): LaskunLahetystapa {
  if (asiakas?.oletusLahetystapa) {
    return asiakas.oletusLahetystapa
  }
  if (asiakas?.sahkoinenosoite?.sahkoinenOsoite) {
    return LaskunLahetystapa.SAHKOINEN
  }
  return LaskunLahetystapa.SAHKOPOSTI
}

export function annaAsiakastyypinOletukset(asiakastyyppi: LaskunAsiakastyyppi, nykyiset: Required<AsiakastyypinOletukset>, tyyppiMuokattavissa: boolean): AsiakastyypinOletukset {
  const oletukset: AsiakastyypinOletukset = {}
  if (asiakastyyppi === LaskunAsiakastyyppi.HENKILO) {
    const maksuaika = nykyiset.maksuaikaValinta === 'muu' ? nykyiset.maksuaikaa : nykyiset.maksuaikaValinta
    const onkoMaksuaikaLiianLyhyt = !maksuaika || maksuaika < KULUTTAJAN_VAHIMMAISMAKSUAIKA
    if (onkoMaksuaikaLiianLyhyt) {
      oletukset.maksuaikaValinta = KULUTTAJAN_VAHIMMAISMAKSUAIKA
      oletukset.maksuaikaa = KULUTTAJAN_VAHIMMAISMAKSUAIKA
    }
    if (tyyppiMuokattavissa) {
      oletukset.tyyppi = LaskunTyypit.TAVALLINEN
    }
    if (nykyiset.oletusLahetystapa === LaskunLahetystapa.SAHKOINEN) {
      oletukset.oletusLahetystapa = LaskunLahetystapa.SAHKOPOSTI
    }
    return oletukset
  }
  if (tyyppiMuokattavissa && !onkoMyynninLajiSallittu(nykyiset.tyyppi, LaskunAsiakastyyppi.YRITYS)) {
    oletukset.tyyppi = LaskunTyypit.TAVALLINEN
  }
  oletukset.oletusLahetystapa = LaskunLahetystapa.SAHKOINEN
  return oletukset
}

export function annaYtjOsoitteenKaytto(lomakkeella: LomakkeenOsoite, ytjstaTaytetty: YtjToimittaja | null, ytjOsoite: YtjToimittaja): YtjOsoitteenKaytto {
  const onkoOsoiteTyhja = !lomakkeella.katuosoite && !lomakkeella.postinro && !lomakkeella.postitoimipaikka
  const onkoOsoiteTaytettyYtjsta = !!ytjstaTaytetty && onkoSamaOsoite(lomakkeella, ytjstaTaytetty)
  if (onkoOsoiteTyhja || onkoOsoiteTaytettyYtjsta) {
    return 'tayta'
  }
  return onkoSamaOsoite(lomakkeella, ytjOsoite) ? 'ei-mitaan' : 'ehdota'
}

function onkoSamaOsoite(lomakkeella: LomakkeenOsoite, osoite: YtjToimittaja): boolean {
  const onkoSamaKatuosoite = (lomakkeella.katuosoite ?? '') === (osoite.katuosoite ?? '')
  const onkoSamaPostinro = (lomakkeella.postinro ?? '') === (osoite.postinro ?? '')
  const onkoSamaPostitoimipaikka = (lomakkeella.postitoimipaikka ?? '') === (osoite.postitmp ?? '')
  return onkoSamaKatuosoite && onkoSamaPostinro && onkoSamaPostitoimipaikka
}

export function voikoVerkkolaskuosoitteenKorvata(nykyinenOsoite: string, haettuOsoite: string | null): boolean {
  const osoite = nykyinenOsoite?.trim()
  const onkoOsoiteTyhja = !osoite
  const onkoOsoiteHaettu = !!osoite && osoite === haettuOsoite
  return onkoOsoiteTyhja || onkoOsoiteHaettu
}

export function voikoVerkkolaskuosoitteenHakea(tiedot: VerkkolaskuosoitteenHaunTiedot): boolean {
  const onkoVerkkolaskuValittuna = tiedot.lahetystapa === LaskunLahetystapa.SAHKOINEN
  const onkoYtunnusKelvollinen = !!tiedot.ytunnus && tiedot.onkoYtunnusKelvollinen
  const onkoYtunnusJoHaettu = tiedot.ytunnus === tiedot.haettuYtunnus
  return onkoVerkkolaskuValittuna && onkoYtunnusKelvollinen && !onkoYtunnusJoHaettu && voikoVerkkolaskuosoitteenKorvata(tiedot.nykyinenOsoite, tiedot.haettuOsoite)
}

export function rakennaAsiakasDialoginTulos(arvot: AsiakasLomake, laskunVastaanottajat: string[], asiakastyyppi: LaskunAsiakastyyppi | null): LaskuAsiakasDialogTulos {
  return {
    nimi: siistiTeksti(arvot.nimi),
    ytunnus: siistiTeksti(arvot.ytunnus),
    katuosoite: siistiTeksti(arvot.katuosoite),
    postinro: siistiTeksti(arvot.postinro),
    postitoimipaikka: siistiTeksti(arvot.postitoimipaikka),
    maa: arvot.maa,
    tyyppi: arvot.tyyppi,
    valuutta: arvot.valuutta,
    kieli: arvot.kieli,
    oletusLahetystapa: arvot.oletusLahetystapa,
    maksuaikaa: arvot.maksuaikaValinta === 'muu' ? arvot.maksuaikaa : arvot.maksuaikaValinta,
    sahkoinenosoite: {
      sahkoinenOsoite: siistiTeksti(arvot.verkkolaskuosoite),
      sahkoinenValittaja: annaValittajanTunnus(arvot.verkkolaskuvalittaja),
    },
    laskunVastaanottajat: [...laskunVastaanottajat],
    asiakastyyppi,
    laskutusprofiiliAvain: arvot.laskutusprofiiliAvain,
  }
}

function annaValittajanTunnus(valittaja: string | Verkkolaskuoperaattori | null): string | null {
  if (!valittaja) {
    return null
  }
  if (typeof valittaja === 'string') {
    return siistiTeksti(valittaja)
  }
  return valittaja.tunnus ?? null
}

function siistiTeksti(teksti: string): string {
  return typeof teksti === 'string' ? teksti.replace(/\s+/g, ' ').trim() : teksti
}
